import { Tooltip } from '@/components/ui/tooltip';
import { getEmailMetadata } from '@/lib/message-metadata';
import {
  Badge,
  Box,
  Button,
  Collapsible,
  FormatByte,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { EMAIL_SOURCE, type MessageData } from '@fluenceur/types';
import DOMPurify from 'dompurify';
import { Attachment, Mail } from 'iconoir-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { EMAIL_STATUS_COLOR } from '../constants';
import { EmailRailNote } from './EmailRailNote';
import MessageContainer from './MessageContainer';
import { getMessageBubbleRadius, type MessageGroupPosition } from './message-bubble-style';

interface EmailMessageProps {
  message: MessageData;
  time: string;
  groupPosition?: MessageGroupPosition;
}

const normalizePreviewText = (value: string) => value.replace(/\s+/g, ' ').trim();

const looksLikeHtml = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const htmlToPlainText = (value: string) => {
  if (!value.trim()) return '';

  if (typeof DOMParser === 'undefined') {
    return normalizePreviewText(value.replace(/<[^>]*>/g, ' '));
  }

  const parser = new DOMParser();
  const decoded = parser.parseFromString(value, 'text/html').documentElement.textContent ?? '';
  const source = looksLikeHtml(decoded) ? decoded : value;
  const parsed = parser.parseFromString(source, 'text/html').body.textContent ?? decoded;

  return normalizePreviewText(parsed);
};

const EmailMessage: React.FC<EmailMessageProps> = ({ message, time, groupPosition = 'single' }) => {
  const { t, i18n } = useTranslation();

  const metadata = getEmailMetadata(message);
  const subject = metadata?.subject || '';
  const deliveryStatus = metadata?.deliveryStatus || 'pending';
  const htmlBody = metadata?.htmlBody || '';
  const statusColor = EMAIL_STATUS_COLOR[deliveryStatus] || 'gray';
  const flowName = metadata?.source === EMAIL_SOURCE.FLOW ? metadata.flowName : null;
  const lastResendEventAt = metadata?.lastResendEventAt;

  const sanitizedHtml = useMemo(
    () => DOMPurify.sanitize(htmlBody, { FORBID_TAGS: ['style'], FORBID_ATTR: ['style'] }),
    [htmlBody]
  );

  const previewText = useMemo(() => {
    const contentPreview = htmlToPlainText(message.content || '');
    if (contentPreview) return contentPreview;

    return htmlToPlainText(sanitizedHtml);
  }, [message.content, sanitizedHtml]);

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }),
    [i18n.language]
  );

  const formattedEventDate = useMemo(() => {
    if (!lastResendEventAt) return null;
    const date = new Date(lastResendEventAt);
    return Number.isNaN(date.getTime()) ? lastResendEventAt : dateFormatter.format(date);
  }, [lastResendEventAt, dateFormatter]);

  const statusBadge = (
    <Badge size="xs" variant="subtle" colorPalette={statusColor} flexShrink={0}>
      {t(`messages.email.${deliveryStatus}`)}
    </Badge>
  );

  return (
    <MessageContainer
      time={time}
      bg="gray.100"
      color="black"
      px={3}
      py={2.5}
      maxW="420px"
      rounded="lg"
      {...getMessageBubbleRadius(false, groupPosition)}
      isSelf={false}
    >
      <VStack gap={2.5} align="stretch">
        <HStack gap={3} align="center" justify="space-between">
          <HStack gap={2} minW={0}>
            <Box color="gray.600" display="inline-flex" flexShrink={0}>
              <Mail width={16} height={16} />
            </Box>
            <Text fontSize="sm" fontWeight="semibold" color="gray.600" truncate>
              {t('messages.email.logLabel')}
            </Text>
          </HStack>
          {formattedEventDate ? (
            <Tooltip
              content={t('messages.email.statusUpdatedAt', { date: formattedEventDate })}
              showArrow
            >
              {statusBadge}
            </Tooltip>
          ) : (
            statusBadge
          )}
        </HStack>

        <VStack gap={1.5} align="stretch">
          <Text fontWeight="semibold" fontSize="15px" color="gray.950" lineClamp={2}>
            {subject || t('messages.email.noSubject')}
          </Text>

          {flowName && (
            <Badge size="xs" variant="subtle" colorPalette="purple" width="fit-content">
              {t('messages.email.sentByFlow', { flowName })}
            </Badge>
          )}

          <EmailRailNote metadata={metadata} />
          {metadata?.attachments?.map((file) => (
            <HStack key={file.id} gap={2} minW={0} fontSize="xs" color="gray.600">
              <Attachment width={14} height={14} />
              <Text truncate>{file.name}</Text>
              <Text flexShrink={0}>
                <FormatByte unitSystem="binary" value={file.size} />
              </Text>
            </HStack>
          ))}

          {previewText && (
            <Text fontSize="15px" color="gray.700" lineClamp={3}>
              {previewText}
            </Text>
          )}
        </VStack>
      </VStack>

      {htmlBody && (
        <Collapsible.Root unmountOnExit>
          <Collapsible.Trigger asChild>
            <Button
              variant="plain"
              size="xs"
              mt={2}
              px={0}
              h="auto"
              minW={0}
              colorPalette="gray"
              fontWeight="semibold"
              alignSelf="flex-start"
            >
              <Collapsible.Context>
                {(api) =>
                  api.open ? t('messages.email.collapseBody') : t('messages.email.expandBody')
                }
              </Collapsible.Context>
            </Button>
          </Collapsible.Trigger>
          <Collapsible.Content>
            <Box
              mt={2}
              p={3}
              bg="white"
              rounded="md"
              fontSize="sm"
              overflow="auto"
              maxH="300px"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          </Collapsible.Content>
        </Collapsible.Root>
      )}
    </MessageContainer>
  );
};

export default EmailMessage;
